import { SagaIterator } from 'redux-saga';
import { all, call, cancel, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { allItemsExistInRange } from '../../../../common/react/SparseCachedList';
import { fetchHomeContent } from '../../../../home/sagas/HomeApiSaga';
import { scheduleSubspaceNameMatchesSchedule } from '../../../../schedule/utils/ScheduleUtils';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ListResult } from '../../../../ts/fetchers/ListResult';
import { Order } from '../../../../ts/helpers/Order';
import { ServerFilterClauses } from '../../../../ts/helpers/ServerFilterClauses';
import { ExplorableItemDefinitions } from '../../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import { FlowOutputStepRunContentType } from '../../../../ts/models/ContentTypes';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IMetricInfo } from '../../../../ts/models/IMetricInfo';
import { IRange } from '../../../../ts/models/IRange';
import { IScheduleInfo } from '../../../../ts/models/IScheduleInfo';
import { ISliceable } from '../../../../ts/models/ISliceable';
import { ISpaceTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { Set } from '../../../../ts/models/Set';
import { favoritesSpaceDefaultSortOrder } from '../../../../ts/models/sort/FavoritesSortOptions';
import { recentsSpaceDefaultSortOrder } from '../../../../ts/models/sort/RecentsSortOptions';
import { areValid, getDefaultOrderForContentTypes } from '../../../../ts/models/sort/SortOptionDefinitions';
import { Content } from '../../../../ts/resources/Content';
import { Favorites } from '../../../../ts/resources/Favorites';
import { GetContentInFavoritesOrderResource } from '../../../../ts/resources/GetContentInFavoritesOrderResource';
import { MetricsResourceQuery } from '../../../../ts/resources/MetricsResourceQuery';
import { Recents } from '../../../../ts/resources/Recents';
import { IResourceQuery, ResourceQuery } from '../../../../ts/resources/ResourceQuery';
import { ViewRecommendations } from '../../../../ts/resources/ViewRecommendations';
import { nonSystemJobFilter } from '../../../../ts/ServerApi/types/IBackgroundJob';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { IFavorite } from '../../../../ts/ServerApi/types/IFavorite';
import { FilterFieldNames, IFieldFilterClause, IFilterClause, IStringValuedFilterClause, IValuedFieldFilterClause } from '../../../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { ClearMixedContentFilters, ContentFilterApplied, ContentTypeFilterSelected, SetContentFilterNoRequestContent, ShowContentTypes, SpaceContentsMissingSliceRequest, SpaceContentsRequest, UpdateFiltersInBothUrlAndState } from '../../../actions/AppActionTypes';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { updateSpaceInfoCountAction } from '../../../actions/spaces/ContentCountsActions';
import { clearFilterContent, ShowContentTypesAction } from '../../../actions/spaces/ContentExplorerFilterActions';
import { deselectAll } from '../../../actions/spaces/ContentExplorerSelectionActions';
import { getFilterClausesForTypes, removeFilterClauseFromFilters } from '../../../actions/spaces/filters/FilterHelpers';
import { SpaceContentsMissingSliceRequestAction, SpaceContentsRequestAction, spaceContentsRequestFailed, spaceContentsRequestSucceeded, spaceContentsSliceSucceeded } from '../../../actions/spaces/RequestSpaceContentsActions';
import { updateMetricsValues } from '../../../actions/spaces/UpdateMetricsActions';
import { selectCurrentlyApplicableContentTypes, selectDefaultContentTypeFilterValues, selectMixedContentFilterClauses, selectSiteSpaceContentType } from '../../../selectors/ContentFilterSelectors';
import { selectContentFromAppState, selectSpaceInfo } from '../../../selectors/ContentSelectors';
import { selectContentSort } from '../../../selectors/ContentSortSelectors';
import { NavLocationSelectors } from '../../../selectors/NavigationStateSelectors';
import { SessionSiteSelectors, SessionUserSelectors } from '../../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { FilterMap } from '../../../state/ContentExplorerState';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../../../state/SiteSpaceContentTypeState';
import {
  DataRoleSubspaceName, dataRoleSubspaceNameToExplorableItemType, DatasourceSubspaceName,
  datasourceSubspaceNameToExplorableItemType,isAllSchedulesSpace, isAllSitesSpace, isBackgroundJobsSpace, isDataRoleSpace,
  isDatasourceSpace, isFavoritesSpace, isFlowSpace, isHomeSpace, isMetricFavoritesSubspace,
  isMetricSpace, isProjectSpace, isRecentsSpace,
  isRecommendationsSpace, isScheduleSpace, isSearchSpace, isSiteSpace,
  isSiteTasksSpace, isSiteUserSpace, isSiteUsersSpace,
  isWorkbookSpace, ScheduleSubspaceName, scheduleSubspaceNameToExplorableItemType,
  SearchSubspaceName, searchSubspaceNameToExplorableItemType, SiteTasksSubspaceName,
  siteTasksSubspaceNameToExplorableItemType, SiteUserSubspaceName, siteUserSubspaceNameToExplorableItemType, SpaceInfo,
  TopLevelProjectId, WorkbookSubspaceName, workbookSubspaceNameToExplorableItemType
} from '../../../state/SpaceInfo';
import { applyMixedContentFilter, clearMixedContentFilters, filterByContentType, setContentFilterNoRequestContent, updateFiltersInBothUrlAndState } from './FilterContentSagas';

// We've chosen these page sizes via "trial and error" to get a reasonably fast user experience
// when changing sort / filter / content type and scrolling in list and card view. (TFS 841819)
export const PAGESIZE_SLICE: number = 600;
export const PAGESIZE_INITIAL_LOAD: number = 100;
// 2019.4 Metrics
export const PAGESIZE_FOR_METRICS: number = 80;

export function* updateQueryParams(contentTypes: ReadonlyArray<string>) {
  const locationService = ServiceLocator.get<ng.ILocationService>('$location');
  const defaultContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = yield select(selectDefaultContentTypeFilterValues);
  const defaultContentTypesSet = Set.fromKeyArray(defaultContentTypeFilterValues);
  const newContentTypesSet = Set.fromKeyArray(contentTypes);

  if (defaultContentTypesSet.containsSameItemsAs(newContentTypesSet)) {
    locationService.search('contentType', null);
  } else {
    locationService.search('contentType', contentTypes.join(','));
  }
  // when updating the url outside Angular, $apply is needed to update the window location.
  ServiceLocator.getAngularActionContext().scope.$apply();
}

namespace InProgressRequestsTracker {
  let inProgressRequestsByStartIndex: { [startIndex: number]: true } = {};

  export function markRequestInProgress(startIndex: number): void {
    inProgressRequestsByStartIndex[startIndex] = true;
  }

  export function isRequestInProgress(startIndex: number): boolean {
    return inProgressRequestsByStartIndex[startIndex];
  }

  export function markRequestComplete(startIndex: number): void {
    delete inProgressRequestsByStartIndex[startIndex];
  }

  export function clear(): void {
    inProgressRequestsByStartIndex = {};
  }
}

export function clearInProgressRequestsForMissingSlices() {
  InProgressRequestsTracker.clear();
}

/**
 * Fetches content for the explorer for the initial slice. Because the page sizes could be different for
 * the initial slice vs. subsequent slices, the initial request is not tracked for de-duping
 * like the missing slice calls are.
 */
export function fetchContentForInitialSlice<T>(resource: ISliceable<T>): ng.IPromise<ListResult<T>> | undefined {
  clearInProgressRequestsForMissingSlices();
  return resource.getSlice(0, PAGESIZE_INITIAL_LOAD);
}

// 2019.4 Metrics
export function fetchMetricsContentForInitialSlice<T>(resource: ISliceable<T>): ng.IPromise<ListResult<T>> | undefined {
  clearInProgressRequestsForMissingSlices();
  return resource.getSlice(0, PAGESIZE_FOR_METRICS);
}

// 2019.4 Metrics
export function fetchMetricsForFavoritesForInitialSlice<T>(resource: ISliceable<T>, pageSize: number): ng.IPromise<ListResult<T>> | undefined {
  clearInProgressRequestsForMissingSlices();
  return resource.getSlice(0, pageSize);
}

// 2019.4 Metrics
/**
 * Fetches the get favorites for 'metric' content type.
 */
export function fetchFavoriteMetrics(): ng.IPromise<ListResult<IFavorite>> | undefined {
  const favoriteMetricsResource = ServiceLocator.get<Favorites>('Favorites');
  const favoriteResourceQuery: ResourceQuery<IFavorite> = favoriteMetricsResource.allMetrics();
  return favoriteResourceQuery.getSlice(0, 1000);
}

/**
 * Fetches content for the explorer for missing slices.
 * This function keeps track of in-progress requests, so that it doesn't make duplicate calls.
 * If it returns a promise, it made a new request; if it returns undefined, it did not make a request
 * because that request is already in progress.
 */
export function fetchContentForMissingSlice<T>(resource: ISliceable<T>, startIndex: number, pageSize: number): ng.IPromise<ListResult<T>> | undefined {
  if (!InProgressRequestsTracker.isRequestInProgress(startIndex)) {
    InProgressRequestsTracker.markRequestInProgress(startIndex);

    return resource.getSlice(startIndex, pageSize).then(result => {
      InProgressRequestsTracker.markRequestComplete(startIndex);
      return result;
    });
  }
}

export function* getFiltersForContentTypes(projectId: string, contentTypes: ReadonlyArray<ExplorableItemType>) {
  const mixedContentFilter = yield select(selectMixedContentFilterClauses);
  const appStateContentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);

  return getFilterClausesForTypes(
    mixedContentFilter,
    contentTypes,
    appStateContentTypes,
    projectId);
}

export function* getSiteResourceQueryForSiteSpaceContentType(siteSpaceContentType: SiteSpaceContentType) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    if (!isSiteSpace(spaceInfo)) {
      return;
    }

    const resource: IResourceQuery<any> = yield call(
      getProjectOrSiteResourceQueryForContentTypes, [convertSiteContentTypeToContentType(siteSpaceContentType)]
    );

    // The project type on the site page means to get all projects not just the top level projects
    if (siteSpaceContentType === 'project') {
      const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
      return resource.withFilters(filters);
    }
    return resource;

  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

/*
 * param orderOverride(optional)- for the pages i.e. home page where storing the order in redux state does not make sense
 */
export function* getRecentsResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1 || contentTypes[0] !== 'view') {
      // we currently only support one type (views)
      return null;

    } else {
      // GetDetailedRecents doesn't support view-core sorts, we will need
      // use a different API depending on the sorts they each support

      if (order.length === 1 && Order.startsWithField(order, recentsSpaceDefaultSortOrder[0].field)) {
        const recents: Recents = ServiceLocator.get<Recents>('Recents');
        return recents.allViews()
          .withOrder(order)
          .withFilters(filtersForContentTypes);

      } else {
        const resource = ExplorableItemDefinitions['view'].getBasicQuery()
          .withFilters(filtersForContentTypes)
          .addFilter(ServerFilterClauses.serverFilterClauses.recent('true'))
          .withOrder(order);

        return resource;
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getFavoritesResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      const resource = ServiceLocator.get<Content>('Content');
      return resource.forFavorites(filtersForContentTypes).withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = ExplorableItemDefinitions[shownContentType].getBasicQuery()
        .withFilters(filtersForContentTypes)
        .addFilter(ServerFilterClauses.serverFilterClauses.favorite('true'))
        .withOrder(order);

      if (shownContentType === 'project') {
        // we want all projects not just top level ones
        const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
        resource = resource.withFilters(filters);
      }

      // Since the regular APIs don't handle the favorite position sort order
      // use GetContentInFavoritesOrder to handle that
      if (order.length === 1 && Order.startsWithField(order, favoritesSpaceDefaultSortOrder[0].field)) {
        return new GetContentInFavoritesOrderResource(resource, shownContentType as ExploreContentApi_ContentType, order[0]);
      }

      return resource;
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getRecommendationsResourceQuery() {
  try {
    const viewRecommendationService = ServiceLocator.get<ViewRecommendations>('ViewRecommendations');
    return viewRecommendationService.viewRecommendations();
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getProjectOrSiteResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);

    if (!(isProjectSpace(spaceInfo) || isSiteSpace(spaceInfo))) {
      return;
    }

    const order: IOrderClause[] = yield call(getOrderClause, contentTypes, spaceInfo);
    const projectId = spaceInfo.item.id;

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      const resource = ServiceLocator.get<Content>('Content');
      return resource.forProject(projectId, filtersForContentTypes).withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = ExplorableItemDefinitions[shownContentType].getBasicQuery();
      resource = resource.withFilters(filtersForContentTypes);
      return resource.withOrder(order);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getOrderClause(currentContentTypeFilterValues: ExplorableItemType[], spaceInfo: SpaceInfo) {
  const order = yield select(selectContentSort);
  if (!areValid(order, currentContentTypeFilterValues, spaceInfo)) {
    return getDefaultOrderForContentTypes(currentContentTypeFilterValues);
  }

  return order;
}

export function* getResourceQuery(spaceInfo: SpaceInfo) {
  try {
    if (isWorkbookSpace(spaceInfo)) {

      const contentType = workbookSubspaceNameToExplorableItemType(spaceInfo.subspaceName as WorkbookSubspaceName);
      if (contentType) {
        let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

        return resource.withOrder(order)
          .withFilters([ServerFilterClauses.serverFilterClauses.workbook(spaceInfo.item.id)]);
      }

    } else if (isProjectSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getProjectOrSiteResourceQueryForContentTypes, contentTypes);

    } else if (isSiteSpace(spaceInfo)) {
      const siteSpaceContentType = yield select(selectSiteSpaceContentType);
      return yield call(getSiteResourceQueryForSiteSpaceContentType, siteSpaceContentType);

    } else if (isDatasourceSpace(spaceInfo)) {
      return yield call(getDatasourceResourceQuery, spaceInfo);

    } else if (isDataRoleSpace(spaceInfo)) {
      return yield call(getDataRoleResourceQuery, spaceInfo);

    } else if (isFavoritesSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getFavoritesResourceQueryForContentTypes, contentTypes);

    } else if (isRecentsSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getRecentsResourceQueryForContentTypes, contentTypes);

    } else if (isRecommendationsSpace(spaceInfo)) {
      return yield call(getRecommendationsResourceQuery);

    } else if (isSearchSpace(spaceInfo)) {
      return yield call(getSearchResourceQuery, spaceInfo);

    } else if (isSiteUserSpace(spaceInfo)) {
      return yield call(getSiteUserResourceQuery, spaceInfo);

    } else if (isSiteUsersSpace(spaceInfo)) {
      return yield call(getAllSiteUsersResourceQuery, spaceInfo);

    } else if (isSiteTasksSpace(spaceInfo)) {
      return yield call(getSiteTasksResourceQuery, spaceInfo);

    } else if (isFlowSpace(spaceInfo)) {
      if (spaceInfo.subspaceName === 'runHistory') {
        const resource = ExplorableItemDefinitions['flowOutputStepRun'].getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['flowOutputStepRun'], spaceInfo);
        const mixedContentFilter = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, FlowOutputStepRunContentType.filterKeys);
        let typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));

        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id));
      }
    } else if (isBackgroundJobsSpace(spaceInfo)) {
      return yield call(getBackgroundJobsQuery, spaceInfo);
    } else if (isMetricSpace(spaceInfo)){
      return yield call(getMetricsResourceQuery, spaceInfo);
    } else if (isAllSitesSpace(spaceInfo)) {
      return yield call(getAllSitesResourceQuery, spaceInfo);
    } else if (isAllSchedulesSpace(spaceInfo)) {
      return yield call(getAllSchedulesResourceQuery, spaceInfo);
    } else if (isScheduleSpace(spaceInfo)) {
      return yield call(getScheduleResourceQuery, spaceInfo);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

// 2019.4 Metrics
export function* getMetricsResourceQuery(spaceInfo: SpaceInfo) {
  const order: IOrderClause[] = yield call(getOrderClause, ['metric'], spaceInfo);
  const filters = yield call(getFiltersForContentTypes, spaceInfo.item.id, ['metric']);

  let query = new MetricsResourceQuery(null)
    .withOrder(order)
    .withFilters(filters);

  if(spaceInfo.subspaceName === 'owned'){
    const ownerUserName = yield select(SessionUserSelectors.sessionUserName);
    query = query.addFilter(ServerFilterClauses.serverFilterClauses.ownerUserName(ownerUserName));
  } else if(FeatureFlags.crassFavorites && spaceInfo.subspaceName === 'favorites') {
    query = query.addFilter(ServerFilterClauses.serverFilterClauses.favorite('true'));
  } else {
    query = query.addFilter(ServerFilterClauses.ContentType(['metric']));
  }
  return query;
}

export function* getBackgroundJobsQuery(spaceInfo: SpaceInfo) {
  // We have both boolean and string type filters in the background jobs space
  let filters: IFieldFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, ['backgroundJob']);

  const isSystemJobsFilterClause = (f: IFieldFilterClause) => {
    return f.field === FilterFieldNames.showSystemJobs;
  };
  const showSystemJobsFilter: IFieldFilterClause | undefined = filters.find(isSystemJobsFilterClause);
  const includeSystemJobs = showSystemJobsFilter
    && (showSystemJobsFilter as IValuedFieldFilterClause<boolean>).value === true;

  // Regardless of truthiness, this filter is a pseudo-filter that shouldn't be in the query
  filters = filters.filter(f => !isSystemJobsFilterClause(f));

  if (includeSystemJobs) {
    // Remove the default filter that filters out any system type job
    filters = filters.filter(f => !shallowEqual(f, nonSystemJobFilter));
  }

  const order: IOrderClause[] = yield call(getOrderClause, ['backgroundJobs'], spaceInfo);
  const resource = ExplorableItemDefinitions['backgroundJob']
    .getBasicQuery()
    .withOrder(order)
    .withFilters(filters);
  return resource;
}

export function* getDatasourceResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = datasourceSubspaceNameToExplorableItemType(spaceInfo.subspaceName as DatasourceSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    return resource.withOrder(order)
      .withFilters([ServerFilterClauses.serverFilterClauses.datasource(spaceInfo.item.id)]);
  }
}

export function* getDataRoleResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = dataRoleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as DataRoleSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    return resource.withOrder(order)
      .withFilters([ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id)]); // TODO (TFSID 940582): Use the correct set of filters for this space
  }
}

export function* getSearchResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = searchSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SearchSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const filtersForContentTypes: IFilterClause[]  = yield call(getFiltersForContentTypes, spaceInfo.item.id, [contentType]);
    resource = resource.withFilters(filtersForContentTypes);
    if (contentType === 'project') {
      // we want to search across all projects not just top level ones
     const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
     resource = resource.withFilters(filters);
    }
    const order: IOrderClause[] = yield call(getOrderClause, [], spaceInfo);
    return resource.withOrder(order);
  }
}

export function* getSiteUserResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = siteUserSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteUserSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const filtersForContentTypesInitial: IFilterClause[]  = yield call(getFiltersForContentTypes, TopLevelProjectId, [contentType]);
    const filtersForContentTypes = removeFilterClauseFromFilters(filtersForContentTypesInitial, FilterFieldNames.topLevelProject);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    const ownerFilter: IFilterClause = spaceInfo.subspaceName === 'alerts'
     ? ServerFilterClauses.serverFilterClauses.user(spaceInfo.item.id)
     : ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id);
    return resource.withOrder(order)
      .withFilters(filtersForContentTypes)
      .addFilter(ownerFilter);
  }
}

export function* getAllSiteUsersResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'siteuser';
  let resource = ExplorableItemDefinitions[contentType].getBasicQuery();

  const filters = _.compact(_.values(ExplorableItemDefinitions[contentType].defaultFilters('')));
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getSiteTasksResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName);
  if (contentType) {
    const filtersForContentTypesInitial: IFilterClause[]  = yield call(getFiltersForContentTypes, null, [contentType]);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

    const siteId: string = yield select(SessionSiteSelectors.siteId);
    const siteIdFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.siteId(siteId);

    const resource = ExplorableItemDefinitions[contentType]
      .getBasicQuery()
      .withOrder(order)
      .withFilters(filtersForContentTypesInitial)
      .addFilter(siteIdFilter);
    return resource;
  }
}

export function* getAllSitesResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'site';
  const resource = ExplorableItemDefinitions[contentType].getBasicQuery();
  const filtersForContentTypes: IFilterClause[]  = yield call(getFiltersForContentTypes, TopLevelProjectId, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filtersForContentTypes);
}

export function* getAllSchedulesResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'schedule';
  let resource = ExplorableItemDefinitions[contentType].getBasicQuery();

  const filters = _.compact(_.values(ExplorableItemDefinitions[contentType].defaultFilters('')));
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getScheduleResourceQuery(spaceInfo: SpaceInfo) {
  const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
  if (explorableItemType) {
    const scheduledAction = (spaceInfo.item as IScheduleInfo).scheduledAction;
    if (!scheduleSubspaceNameMatchesSchedule(spaceInfo.subspaceName as ScheduleSubspaceName, scheduledAction)) {
      return;
    }

    let resource = ExplorableItemDefinitions[explorableItemType].getBasicQuery();

    const filters = _.compact(_.values(ExplorableItemDefinitions[explorableItemType].defaultFilters('')));
    const inServerArea = yield select(NavLocationSelectors.inServerArea);
    if (!inServerArea) {
      const siteId: string = yield select(SessionSiteSelectors.siteId);
      const siteIdFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.siteId(siteId);
      filters.push(siteIdFilter);
    }

    const scheduleIdFilter = ServerFilterClauses.serverFilterClauses.schedule(spaceInfo.item.id);
    filters.push(scheduleIdFilter);

    const order: IOrderClause[] = yield call(getOrderClause, [explorableItemType], spaceInfo);
    return resource
      .withOrder(order)
      .withFilters(filters);
  }
}

export function* processRequestSpaceContent(action: SpaceContentsRequestAction) {
  const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

  try {
    if (isHomeSpace(spaceInfo)) {
      // Clear search filters before fetching content
      yield put(clearFilterContent());
      yield call(fetchHomeContent);

    } else {

      // on the search space if there is no search term then clear any items and don't get a resource query
      if (isSearchSpace(spaceInfo)) {
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        if (_.isEmpty(mixedContentFilter) || !mixedContentFilter.search || (mixedContentFilter.search as IStringValuedFilterClause).value.length === 0) {
          const noContent: ListResult<IContentInfo> = ListResult.empty<IContentInfo>();
          yield put(spaceContentsRequestSucceeded(noContent));
          return;
        }
      }

      const resource = yield call(getResourceQuery, spaceInfo);

      yield put(deselectAll());

      // for cases in which there is no content to fetch,
      // resource can be undefined (I.E. NLP)
      if (!_.isUndefined(resource)) {

        let content: ListResult<IContentInfo>;

        // 2019.4 Metrics
        // If we are in the metrics space, we need to call getFavorites() to determine which metrics are actually favorited or not
        if (isMetricSpace(spaceInfo)) {
          content = yield call(fetchInitialMetricContent, spaceInfo, resource);
        } else {
          content = yield call(fetchContentForInitialSlice, resource);
        }

        if (content) {
          yield put(spaceContentsRequestSucceeded(content));

          // update individual count for project space by setting the specific content type as a subspacename
          if (isProjectSpace(spaceInfo)) {
            const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
            if (contentTypes.length === 1) {
              const projectSpaceInfo: SpaceInfo = { ...spaceInfo, subspaceName: contentTypes[0] };
              yield put(updateSpaceInfoCountAction(projectSpaceInfo, content.total));
            }
          } else {
            yield put(updateSpaceInfoCountAction(spaceInfo, content.total));

            if (isMetricSpace(spaceInfo)) {
              yield put(updateMetricsValues(content.items as IMetricInfo[]));
            }
          }
        }
      }
    }
  } catch (e) {
    yield put(spaceContentsRequestFailed());
    const errorMessage = getErrorMessage(spaceInfo, e);
    yield put(requestFailed(e, errorMessage));
  }
}

// 2019.4 Metrics
export function* fetchInitialMetricContent(spaceInfo: SpaceInfo, resource: IResourceQuery<any>) {
  let results;
  if (isMetricFavoritesSubspace(spaceInfo)) {
    // We just need the total number of metrics (TFSID: 961220)
    const initialResults = yield call(fetchMetricsForFavoritesForInitialSlice, resource, 0);
    const totalMetrics = initialResults.total;

    results = yield all([call(fetchMetricsForFavoritesForInitialSlice, resource, totalMetrics), call(fetchFavoriteMetrics)]);
  } else {
    results = yield all([call(fetchMetricsContentForInitialSlice, resource), call(fetchFavoriteMetrics)]);
  }

  return updateMetricContentWithFavoriteInfo(results, spaceInfo);
}

function updateMetricContentWithFavoriteInfo(results: any, spaceInfo: SpaceInfo) {
  let content = results[0];
  const favorites: ListResult<IFavorite> = results[1];

  // if there isn't any content, then just return -- we don't need to do anything here
  if (!content) {
    return;
  }

  // If we are in the favorited space, then we only want to show the favorited metrics
  if (spaceInfo.subspaceName === 'favorites') {
    let favoritedItems: IContentInfo[] = content.items.filter((contentItem: IContentInfo) => {
      const isFavoritedItem: boolean = _.any(favorites.items, (favoriteItem: IFavorite) => {
        return favoriteItem.objectId === String(contentItem.id);
      });
      contentItem.favorite = isFavoritedItem;
      return isFavoritedItem;
    });
    content = new ListResult<IContentInfo>(favoritedItems);

  } else {
    // all other spaces we just need to mark if the item is favorited or not
    content.items.forEach((item: IContentInfo) => {
      const id = item.id;
      const isFavoritedItem: boolean = _.any(favorites.items, (favoriteItem: IFavorite) => {
        return favoriteItem.objectId === String(id);
      });
      item.favorite = isFavoritedItem;
    });
  }
  return content;
}

function getErrorMessage(spaceInfo: SpaceInfo, e: Error): string {
  if (isSearchSpace(spaceInfo)) {
    switch(spaceInfo.subspaceName) {
      case 'tables':
        return Messages.catalogingTablesSearchErrorMessage();
      case 'databases':
        return Messages.catalogingDatabasesSearchErrorMessage();
    }
  }

  return Messages.errorUnknown();
}

/**
 * Missing slices are valid only for the current explorer query, which is set up via various actions
 * (those that set the order, filters, etc. in the store) but initiated by a SpaceContentsRequest
 * (that is where the current space contents get cleared out and re-populated with the contents of
 * the new query.
 *
 * Hence, if we ever get a SpaceContentsRequest, all pending missing slice requests must be cancelled,
 * otherwise slices from the old query would be inserted into the new query's results.
 */
export function* processRequestSpaceContentMissingSliceWithCancellation(pageSize: number, action: SpaceContentsMissingSliceRequestAction) {
  const missingSliceTask = yield fork(processRequestSpaceContentMissingSliceInner, pageSize, action);

  yield take(SpaceContentsRequest);
  yield cancel(missingSliceTask);
}

export function* processRequestSpaceContentMissingSliceInner(pageSize: number, action: SpaceContentsMissingSliceRequestAction) {
  const itemRange: IRange = { start: action.start, end: action.end };

  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const resource = yield call(getResourceQuery, spaceInfo);

    // 2019.4 Metrics
    if (isMetricSpace(spaceInfo)) {
      // For Metrics Space, the metrics service cannot handle large requests so we are limiting the page size
      pageSize = PAGESIZE_FOR_METRICS;
    }

    const pageIndexOfFirstItem = Math.floor(itemRange.start / pageSize);
    const pageIndexOfLastItem = Math.floor(itemRange.end / pageSize);

    const items: ReadonlyArray<IContentInfo> = yield select(selectContentFromAppState);

    for (let pageIndex = pageIndexOfFirstItem; pageIndex <= pageIndexOfLastItem; pageIndex++) {

      const startIndexToFetch = pageSize * pageIndex;
      const endIndexToFetch = startIndexToFetch + pageSize - 1;

      if (!allItemsExistInRange(items, startIndexToFetch, endIndexToFetch)) {

        let content: ListResult<IContentInfo>;

        // 2019.4 Metrics
        // If we are in the metrics space, we need to call getFavorites() to determine which metrics are actually favorited or not
        if (isMetricSpace(spaceInfo)) {
          content = yield call(fetchMissingSliceMetricContent, spaceInfo, resource, startIndexToFetch, pageSize);
        } else {
          content = yield call(fetchContentForMissingSlice, resource, startIndexToFetch, pageSize);
        }


        if (content) {
          yield put(spaceContentsSliceSucceeded(startIndexToFetch, content));

          // we need to call again for metric data after fetching more
          // items from content management
          if (isMetricSpace(spaceInfo)) {
            yield put(updateMetricsValues(content.items as IMetricInfo[]));
          }
        }
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// 2019.4 Metrics
function* fetchMissingSliceMetricContent(spaceInfo: SpaceInfo, resource: IResourceQuery<any>, startIndex: number, pageSize: number) {
  // CRASS asks for the next page and not the next page index (eg. 0 returns the first pageSize, 1 returns the second pageSize)
  // We need to divide the start index (nth item) by the page size to find the 'page' number for CRASS.
  let results = yield all([call(fetchContentForMissingSlice, resource, Math.floor(startIndex / pageSize), pageSize), call(fetchFavoriteMetrics)]);
  return updateMetricContentWithFavoriteInfo(results, spaceInfo);
}

export function* processShowContentTypes(action: ShowContentTypesAction) {
  try {
    yield call(updateQueryParams, action.contentTypes);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* rootMixedContentSaga(): SagaIterator {
  yield takeLatest(SpaceContentsRequest, processRequestSpaceContent);
  yield takeLatest(ContentTypeFilterSelected, filterByContentType);
  yield takeLatest(ShowContentTypes, processShowContentTypes);
  yield takeEvery(SpaceContentsMissingSliceRequest, processRequestSpaceContentMissingSliceWithCancellation, PAGESIZE_SLICE);
  yield takeLatest(ContentFilterApplied, applyMixedContentFilter);
  yield takeLatest(SetContentFilterNoRequestContent, setContentFilterNoRequestContent);
  yield takeLatest(ClearMixedContentFilters, clearMixedContentFilters);
  yield takeEvery(UpdateFiltersInBothUrlAndState, updateFiltersInBothUrlAndState);
}
